import React, { useCallback, useState } from 'react';
import type { RefObject } from 'react';
import type { FilterableTableHandle } from '../components/ui';

/**
 * Orchestration des actions bulk quand la sélection peut contenir des éléments
 * HORS du filtre courant (keepSelectionOnFilter). Réutilisé par les pages qui
 * ont une sélection persistante + actions bulk (GroundTruthPage, DocumentsPage).
 *
 * Flux : on appelle `requestBulk(run)`. Le hook compare la sélection aux clés
 * filtrées (via le handle du tableau) :
 *  - si TOUS les sélectionnés sont visibles → exécute `run(selectedKeys)` direct ;
 *  - sinon → ouvre un modal « Tous (N) / Visibles seulement (M) / Annuler ».
 * `run` reçoit la liste des clés à traiter selon le choix.
 */
export interface BulkScope {
  /** Déclenche une action bulk avec garde hors-filtre. */
  requestBulk: (run: (keys: Array<string | number>) => void | Promise<void>) => void;
  /** Élément modal à monter dans le JSX de la page. */
  modal: React.ReactNode;
}

export function useBulkSelectionScope(
  selectedKeys: Set<string | number>,
  tableRef: RefObject<FilterableTableHandle>,
): BulkScope {
  const [pending, setPending] = useState<{
    run: (keys: Array<string | number>) => void | Promise<void>;
    all: Array<string | number>;
    visible: Array<string | number>;
    hiddenCount: number;
  } | null>(null);

  const requestBulk = useCallback(
    (run: (keys: Array<string | number>) => void | Promise<void>) => {
      const all = Array.from(selectedKeys);
      const filtered = new Set(tableRef.current?.getFilteredKeys() ?? []);
      const visible = all.filter((k) => filtered.has(k));
      const hiddenCount = all.length - visible.length;
      if (hiddenCount === 0) {
        // Toute la sélection est visible → pas d'ambiguïté, on exécute.
        run(all);
        return;
      }
      setPending({ run, all, visible, hiddenCount });
    },
    [selectedKeys, tableRef],
  );

  const close = () => setPending(null);

  const modal = pending ? (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[15vh]">
      <div className="absolute inset-0 bg-black/50" onClick={close} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Sélection hors filtre</h2>
        <p className="text-sm text-gray-600 mb-5">
          {pending.all.length} document(s) sélectionné(s), dont{' '}
          <span className="font-semibold text-orange-700">{pending.hiddenCount}</span> hors du
          filtre actuellement appliqué. Appliquer l'action à quels documents ?
        </p>
        <div className="flex flex-col gap-2">
          <button
            className="w-full px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 text-sm"
            onClick={() => { const p = pending; close(); void p.run(p.all); }}
          >
            Tous les sélectionnés ({pending.all.length})
          </button>
          <button
            className="w-full px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm"
            onClick={() => { const p = pending; close(); void p.run(p.visible); }}
          >
            Visibles seulement ({pending.visible.length})
          </button>
          <button
            className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
            onClick={close}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { requestBulk, modal };
}
